import axios from "axios";
import { useState, useEffect } from "react";
import { API_V7_URL } from "../../constants/Globals";
import moment from "moment";

const getStepDisplayName = (step) => {
  const partType = (step?.part?.partType || "").toLowerCase();
  if (partType === "poll" || partType === "question" || partType === "quiz") return "Poll";
  if (partType === "form") return "Form";
  if (partType === "cta") return "CTA";
  if (partType === "product") return "Product";
  if (partType === "sign-up") return "Sign-Up";
  return step?.part?.partType || "Unknown";
};

const getStepStats = (step) => {
  return {
    starts: step.starts ?? 0,
    percentCompleted: step.percentCompleted ?? null,
    completions: {
      votes: step.votes ?? 0,
      productClicks: step.productClicks ?? 0,
      ctaClicks: step.ctaClicks ?? 0,
      formSubmits: step.formSubmits ?? 0,
      signUpClicks: step.signUpClicks ?? 0,
    },
  };
};

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? "" : d.toLocaleDateString();
};

const getCompletionLabel = (step, stats) => {
  const partType = (step?.part?.partType || "").toLowerCase();
  if (partType === "poll" || partType === "question" || partType === "quiz") {
    const totalPollViewed = step.pollViewed ?? 0;
    const totalVotes = stats.completions.votes ?? 0;
    const percent =
      totalPollViewed > 0
        ? ((totalVotes * 100) / totalPollViewed).toFixed(1)
        : "0";
    return `(${totalPollViewed} poll views -> ${totalVotes} votes (${percent}%))`;
  }
  if (partType === "cta") {
    const ctaViewed = step.ctaViewed ?? 0;
    const ctaClicks = stats.completions.ctaClicks ?? 0;
    const percent = ctaViewed > 0 ? ((ctaClicks * 100) / ctaViewed).toFixed(1) : "0";
    return `(${ctaViewed} Cta views -> ${ctaClicks} clicks (${percent}%))`;
  }
  if (partType === "form") {
    const formViewed = step.formViewed ?? 0;
    const formSubmits = stats.completions.formSubmits ?? 0;
    const percent = formViewed > 0 ? ((formSubmits * 100) / formViewed).toFixed(1) : "0";
    return `(${formViewed} Form views -> ${formSubmits} submits (${percent}%))`;
  }
  if (partType === "product") {
    const productViewed = step.productViewed ?? 0;
    const productClicks = stats.completions.productClicks ?? 0;
    const percent = productViewed > 0 ? ((productClicks * 100) / productViewed).toFixed(1) : "0";
    return `(${productViewed} Product views -> ${productClicks} clicks (${percent}%))`;
  }
  if (partType === "sign-up") {
    const signUpViewed = step.signUpViewed ?? 0;
    const signUpClicks = stats.completions.signUpClicks ?? 0;
    const percent =
      signUpViewed > 0 ? ((signUpClicks * 100) / signUpViewed).toFixed(1) : "0";
    return `(${signUpViewed} Sign-up views -> ${signUpClicks} clicks (${percent}%))`;
  }
  // Fallback for other types
  const parts = [];
  if (stats.completions.votes > 0) parts.push(`Votes: ${stats.completions.votes}`);
  if (stats.completions.productClicks > 0) parts.push(`Product clicks: ${stats.completions.productClicks}`);
  if (stats.completions.ctaClicks > 0) parts.push(`Cta clicks: ${stats.completions.ctaClicks}`);
  if (stats.completions.formSubmits > 0) parts.push(`Forms: ${stats.completions.formSubmits}`);
  if (stats.completions.signUpClicks > 0) parts.push(`Sign-up clicks: ${stats.completions.signUpClicks}`);
  return parts.length > 0 ? `(${parts.join(", ")})` : "";
};

const SequenceAnalytics = ({ sequenceId }) => {
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!sequenceId) return;
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(
          `${API_V7_URL}/api/sequence/steps/analytics?sequenceId=${sequenceId}`
        );
        const rows = response?.data?.data?.rows;
        const stepList = Array.isArray(rows) ? rows : rows?.steps ?? [];
        setSteps(stepList);
      } catch (err) {
        console.error("Failed to fetch sequence analytics:", err);
        setError("Failed to load analytics.");
        setSteps([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [sequenceId]);

  const sortedSteps = [...steps].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  if (loading) {
    return (
      <div className="settings-tab-box" style={{ padding: "1rem" }}>
        <p>Loading analytics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="settings-tab-box" style={{ padding: "1rem" }}>
        <div className="alert alert-danger">{error}</div>
      </div>
    );
  }

  return (
    <div
      className="settings-tab-box"
      style={{
        borderRadius: "5px",
        padding: "1rem",
        paddingLeft: "0px",
      }}
    >
      <h5 className="mb-4">Sequence Analytics</h5>
      {sortedSteps.length === 0 ? (
        <p className="text-muted">No steps in this sequence yet.</p>
      ) : (
        <div>
          {sortedSteps.map((step, index) => {
            const stats = getStepStats(step);
            const totalCompletions =
              stats.completions.votes +
              stats.completions.productClicks +
              stats.completions.ctaClicks +
              stats.completions.formSubmits +
              stats.completions.signUpClicks;

            const starts = typeof stats.starts === "number" ? stats.starts : 0;
            const partType = (step?.part?.partType || "").toLowerCase();
            let pct = 0;
            if (partType === "poll" || partType === "question" || partType === "quiz") {
              const totalPollViewed = step.pollViewed ?? 0;
              const totalVotes = stats.completions.votes ?? 0;
              pct = totalPollViewed > 0 ? parseFloat(((totalVotes * 100) / totalPollViewed).toFixed(1)) : 0;
            } else if (partType === "cta") {
              const ctaViewed = step.ctaViewed ?? 0;
              const ctaClicks = stats.completions.ctaClicks ?? 0;
              pct = ctaViewed > 0 ? parseFloat(((ctaClicks * 100) / ctaViewed).toFixed(1)) : 0;
            } else if (partType === "form") {
              const formViewed = step.formViewed ?? 0;
              const formSubmits = stats.completions.formSubmits ?? 0;
              pct = formViewed > 0 ? parseFloat(((formSubmits * 100) / formViewed).toFixed(1)) : 0;
            } else if (partType === "product") {
              const productViewed = step.productViewed ?? 0;
              const productClicks = stats.completions.productClicks ?? 0;
              pct = productViewed > 0 ? parseFloat(((productClicks * 100) / productViewed).toFixed(1)) : 0;
            } else if (partType === "sign-up") {
              const signUpViewed = step.signUpViewed ?? 0;
              const signUpClicks = stats.completions.signUpClicks ?? 0;
              pct =
                signUpViewed > 0
                  ? parseFloat(((signUpClicks * 100) / signUpViewed).toFixed(1))
                  : 0;
            }

            const completionLabel = getCompletionLabel(step, stats);

            return (
              <div className="usage-block mb-3" key={step.id}>
                <div
                  style={{ height: "40px", fontSize: "20px", fontWeight: "640" }}
                >
                  Step {index + 1}: {getStepDisplayName(step)}
                </div>
                <div
                  className="d-flex justify-content-between align-items-center"
                  style={{ height: "30px", fontSize: "15px", fontWeight: "600" }}
                >
                  <span>
                    Starts: {moment(step.createdAt)
                                      .add(1, "days")
                                      .format("MMM DD, YYYY hh:mm A")}
                  </span>
                  {completionLabel && (
                    <span
                      style={{
                        fontSize: "13px",
                        fontWeight: "400",
                        color: "#6c757d",
                      }}
                    >
                      Completions {completionLabel}
                    </span>
                  )}
                </div>

                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <div
                      className="progress"
                      style={{ height: 10, width: "100%" }}
                    >
                      <div
                        className="progress-bar bg-primary"
                        role="progressbar"
                        style={{
                          width: `${pct}%`,
                        }}
                        aria-valuenow={totalCompletions}
                        aria-valuemin={0}
                        aria-valuemax={starts}
                      ></div>
                    </div>
                  </div>

                  <span
                    style={{
                      marginLeft: "12px",
                      fontSize: "14px",
                      fontWeight: "600",
                      minWidth: "50px",
                      textAlign: "right",
                    }}
                  >
                    {pct}%
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SequenceAnalytics;
